/**
 * Color Constants - Central Color Palette
 *
 * This file serves as the color resource dictionary for the application,
 * similar to XAML's ResourceDictionary. All colors are defined here and
 * can be referenced throughout the application.
 *
 * Colors are synced from Figma design system (1. Colours collection)
 *
 * Usage:
 * import { Colors } from '../constants/colors';
 *
 * Then use: Colors.Gray300, Colors.PrimaryPurple60, etc.
 */
export const Colors = {
    // ═══════════════════════════════════════════════════════════════════════════
    // PRIMARY PURPLE SCALE (Brand Primary)
    // ═══════════════════════════════════════════════════════════════════════════
    PrimaryPurple05: "#F8F7FF",
    PrimaryPurple10: "#F1F0FF",
    PrimaryPurple15: "#E3E0FB",
    PrimaryPurple20: "#D4D0F6",
    PrimaryPurple30: "#BFB9F1",
    PrimaryPurple40: "#A9A1ED",
    PrimaryPurple50: "#8479E2",
    PrimaryPurple60: "#6050D7",
    PrimaryPurple70: "#3B28CC",
    PrimaryPurple80: "#2E1DA4",
    PrimaryPurple90: "#21137C",
    PrimaryPurple100: "#130854",
    // Legacy alias
    PurpleSparkle: "#3B28CC", // Same as PrimaryPurple70
    // ═══════════════════════════════════════════════════════════════════════════
    // GRAY SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Gray100: "#F9FAFB",
    Gray200: "#F3F4F6",
    Gray300: "#E5E7EB",
    Gray400: "#D1D5DB",
    Gray500: "#9CA3AF",
    Gray600: "#6B7280",
    Gray700: "#4B5563",
    Gray800: "#374151",
    Gray900: "#1F2937",
    Gray990: "#111827",
    // ═══════════════════════════════════════════════════════════════════════════
    // BLUE SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Blue10: "#EFF6FF",
    Blue20: "#DBEAFE",
    Blue30: "#BFDBFE",
    Blue40: "#93C5FD",
    Blue50: "#60A5FA",
    Blue60: "#3B82F6",
    Blue70: "#2563EB",
    Blue80: "#1D4ED8",
    Blue90: "#1E40AF",
    Blue99: "#1E3A8A",
    // ═══════════════════════════════════════════════════════════════════════════
    // GREEN SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Green10: "#ECFDF5",
    Green20: "#D1FAE5",
    Green30: "#A7F3D0",
    Green40: "#6EE7B7",
    Green50: "#34D399",
    Green60: "#10B981",
    Green70: "#059669",
    Green80: "#047857",
    Green90: "#065F46",
    Green99: "#064E3B",
    // ═══════════════════════════════════════════════════════════════════════════
    // RED SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Red10: "#FEF2F2",
    Red20: "#FEE2E2",
    Red30: "#FECACA",
    Red40: "#FCA5A5",
    Red50: "#F87171",
    Red60: "#EF4444",
    Red70: "#DC2626",
    Red80: "#B91C1C",
    Red90: "#991B1B",
    Red99: "#7F1D1D",
    // ═══════════════════════════════════════════════════════════════════════════
    // YELLOW SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Yellow10: "#FFFBEB",
    Yellow20: "#FEF3C7",
    Yellow30: "#FDE68A",
    Yellow40: "#FCD34D",
    Yellow50: "#FBBF24",
    Yellow60: "#F59E0B",
    Yellow70: "#D97706",
    Yellow80: "#B45309",
    Yellow90: "#92400E",
    Yellow99: "#78350F",
    // ═══════════════════════════════════════════════════════════════════════════
    // INDIGO SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Indigo10: "#EEF2FF",
    Indigo20: "#E0E7FF",
    Indigo30: "#C7D2FE",
    Indigo40: "#A5B4FC",
    Indigo50: "#818CF8",
    Indigo60: "#6366F1",
    Indigo70: "#4F46E5",
    Indigo80: "#4338CA",
    Indigo90: "#3730A3",
    Indigo99: "#312E81",
    // ═══════════════════════════════════════════════════════════════════════════
    // PINK SCALE
    // ═══════════════════════════════════════════════════════════════════════════
    Pink10: "#FDF2F8",
    Pink20: "#FCE7F3",
    Pink30: "#FBCFE8",
    Pink40: "#F9A8D4",
    Pink50: "#F472B6",
    Pink60: "#EC4899",
    Pink70: "#DB2777",
    Pink80: "#BE185D",
    Pink90: "#9D174D",
    Pink99: "#831843",
    // ═══════════════════════════════════════════════════════════════════════════
    // PURPLE SCALE (Secondary - different from Primary Purple)
    // ═══════════════════════════════════════════════════════════════════════════
    Purple10: "#F5F3FF",
    Purple20: "#EDE9FE",
    Purple30: "#DDD6FE",
    Purple40: "#C4B5FD",
    Purple50: "#A78BFA",
    Purple60: "#8B5CF6",
    Purple70: "#7C3AED",
    Purple80: "#6D28D9",
    Purple90: "#5B21B6",
    Purple99: "#4C1D95",
    // ═══════════════════════════════════════════════════════════════════════════
    // SEMANTIC / LEGACY COLORS
    // ═══════════════════════════════════════════════════════════════════════════
    // Neutral
    NeutralPain: "#32383E",
    // Black Variants (aliases for gray)
    Black30: "#E5E7EB", // Same as Gray300
    Black90: "#1F2937", // Same as Gray900
    // Background Colors
    Background: "#282828",
    VersionBackground: "#134074",
    MainBackground: "#FFFFFF",
    // Progress Bar
    ProgressBarProgress: "#06B025",
    ProgressBarBackground: "#E6E6E6",
    ProgressBarBorder: "#BCBCBC",
    // ═══════════════════════════════════════════════════════════════════════════
    // LEGACY ALIASES (for backwards compatibility)
    // These map old color names to their new equivalents
    // ═══════════════════════════════════════════════════════════════════════════
    /** @deprecated Use PrimaryPurple05 instead */
    PrimaryPurple5: "#F8F7FF",
};
/**
 * Helper function to get color with optional opacity
 *
 * @param colorKey - The color key from Colors object
 * @param opacity - Optional opacity value (0-1)
 * @returns CSS color string
 *
 * @example
 * getColor('Gray300') // "#E5E7EB"
 * getColor('Gray300', 0.5) // "rgba(229, 231, 235, 0.5)"
 */
export function getColor(colorKey, opacity) {
    const color = Colors[colorKey];
    if (opacity === undefined) {
        return color;
    }
    // Convert hex to rgba
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
/**
 * Common color combinations for easy access
 */
export const ColorCombos = {
    // Borders
    borderDefault: Colors.Gray300,
    borderLight: Colors.Gray200,
    borderDark: Colors.Gray400,
    // Backgrounds
    bgPrimary: Colors.MainBackground,
    bgSecondary: Colors.Gray100,
    bgTertiary: Colors.Gray200,
    bgPurpleLight: Colors.PrimaryPurple05,
    // Text
    textPrimary: Colors.Gray990,
    textSecondary: Colors.Gray600,
    textTertiary: Colors.Gray500,
    textDisabled: Colors.Gray400,
    // States
    success: Colors.Green60,
    successBg: Colors.Green10,
    warning: Colors.Yellow60,
    warningBg: Colors.Yellow10,
    error: Colors.Red60,
    errorBg: Colors.Red10,
    info: Colors.Blue70,
    infoBg: Colors.Blue10,
    // Brand
    brandPrimary: Colors.PrimaryPurple60,
    brandHover: Colors.PrimaryPurple70,
    brandActive: Colors.PrimaryPurple80,
    brandLight: Colors.PrimaryPurple10,
};
/**
 * Export default for convenient importing
 */
export default Colors;
